import { useState } from 'react';
import type { Product, ProductKind, Supplier } from '@salonomy/types';
import { Button, Field, Modal, Select, TextInput, Toggle } from '@salonomy/ui';
import { useCreateProduct, useUpdateProduct } from '../../data/stock.js';
import { useToast } from '../../lib/toast.js';
import { penceToPounds, poundsToPence } from '../services/poundsPence.js';
import './stock.css';

const KINDS: Array<{ value: ProductKind; label: string }> = [
  { value: 'retail', label: 'Retail (sold to clients)' },
  { value: 'professional', label: 'Professional (back-bar use)' },
];

/**
 * Create / edit a product. Two distinct shapes, deliberately:
 *  - CREATE carries `openingStock` — the server turns a positive opening count into a 'receive'
 *    movement, so the new product starts with on-hand. Cost/price cross the £↔pence edge via
 *    poundsPence at the form boundary only.
 *  - EDIT is ATTRIBUTES ONLY. There is NO quantity field — on-hand is server-derived and is moved
 *    exclusively through a stock movement. Editing a product can never change its stock. `active` is
 *    an attribute here (the toggle), with a separate Archive control on the list for the soft delete.
 */
export function ProductModal({
  existing,
  suppliers,
  onClose,
}: {
  existing?: Product;
  suppliers: Supplier[];
  onClose: () => void;
}) {
  const create = useCreateProduct();
  const update = useUpdateProduct();
  const toast = useToast();
  const editing = Boolean(existing);

  const [name, setName] = useState(existing?.name ?? '');
  const [kind, setKind] = useState<ProductKind>(existing?.kind ?? 'retail');
  const [supplierId, setSupplierId] = useState(existing?.supplierId ?? '');
  const [sku, setSku] = useState(existing?.sku ?? '');
  const [barcode, setBarcode] = useState(existing?.barcode ?? '');
  const [cost, setCost] = useState(penceToPounds(existing?.costPence ?? 0));
  const [price, setPrice] = useState(penceToPounds(existing?.pricePence ?? 0));
  const [vatable, setVatable] = useState(existing?.vatable ?? true);
  const [threshold, setThreshold] = useState(String(existing?.lowStockThreshold ?? 0));
  const [trackStock, setTrackStock] = useState(existing?.trackStock ?? true);
  const [active, setActive] = useState(existing?.active ?? true);
  // CREATE only — never shown on the edit form (stock is never patched).
  const [openingStock, setOpeningStock] = useState('0');
  const [busy, setBusy] = useState(false);

  // Only active suppliers are offered for a NEW assignment, but keep the existing one selectable even
  // if it has since been deactivated (so an edit doesn't silently drop it).
  const supplierOptions = suppliers.filter((s) => s.active || s.id === existing?.supplierId);

  async function submit() {
    if (!name.trim()) {
      toast.error('A product name is required.');
      return;
    }
    const costPence = poundsToPence(cost) ?? 0;
    const pricePence = poundsToPence(price) ?? 0;
    const lowStockThreshold = Math.max(0, Math.trunc(Number(threshold) || 0));

    setBusy(true);
    try {
      if (editing && existing) {
        // ATTRIBUTES ONLY — no quantity. `active` rides along as an attribute.
        await update.mutateAsync({
          id: existing.id,
          body: {
            name: name.trim(),
            kind,
            supplierId: supplierId || null,
            sku: sku.trim() || null,
            barcode: barcode.trim() || null,
            costPence,
            pricePence,
            vatable,
            lowStockThreshold,
            trackStock,
            active,
          },
        });
        toast.success('Product updated');
      } else {
        const opening = Math.max(0, Math.trunc(Number(openingStock) || 0));
        await create.mutateAsync({
          name: name.trim(),
          kind,
          supplierId: supplierId || null,
          sku: sku.trim() || null,
          barcode: barcode.trim() || null,
          costPence,
          pricePence,
          vatable,
          lowStockThreshold,
          trackStock,
          openingStock: opening,
        });
        toast.success('Product added');
      }
      onClose();
    } catch {
      toast.error('Could not save the product.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit product' : 'Add product'}
      width={560}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {editing ? 'Save' : 'Add product'}
          </Button>
        </>
      }
    >
      <Field label="Name">
        <TextInput
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Olaplex No.3"
        />
      </Field>

      <div className="stock-modal-grid">
        <Field label="Kind" hint="Retail sells to clients; professional is back-bar.">
          <Select value={kind} onChange={(e) => setKind(e.target.value as ProductKind)}>
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Supplier" hint={supplierOptions.length ? undefined : 'No suppliers yet.'}>
          <Select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            <option value="">No supplier</option>
            {supplierOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {!s.active ? ' (inactive)' : ''}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="stock-modal-grid">
        <Field label="SKU" hint="Optional internal code.">
          <TextInput value={sku} onChange={(e) => setSku(e.target.value)} />
        </Field>
        <Field label="Barcode" hint="Optional — scannable at the till.">
          <TextInput value={barcode} onChange={(e) => setBarcode(e.target.value)} />
        </Field>
      </div>

      <div className="stock-modal-grid">
        <Field label="Cost (£)" hint="What you pay your supplier per unit.">
          <TextInput
            inputMode="decimal"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            placeholder="0.00"
          />
        </Field>
        <Field label="Retail price (£)" hint="What a client pays.">
          <TextInput
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="0.00"
          />
        </Field>
      </div>

      <div className="stock-modal-grid">
        <Field label="Low-stock threshold" hint="Flags as low at or below this on-hand.">
          <TextInput
            inputMode="numeric"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
          />
        </Field>
        {!editing ? (
          <Field label="Opening stock" hint="Becomes a 'receive' movement — your starting on-hand.">
            <TextInput
              inputMode="numeric"
              value={openingStock}
              onChange={(e) => setOpeningStock(e.target.value)}
            />
          </Field>
        ) : (
          <div />
        )}
      </div>

      <div className="stock-toggles">
        <Toggle checked={vatable} onChange={setVatable} label="VAT applies" />
        <Toggle checked={trackStock} onChange={setTrackStock} label="Track stock" />
        {editing ? <Toggle checked={active} onChange={setActive} label="Active" /> : null}
      </div>

      {editing ? (
        <div className="stock-note muted">
          On-hand isn&rsquo;t edited here — it&rsquo;s the running sum of stock movements. To change
          the count, record a movement (receive, waste, count correction or return).
        </div>
      ) : null}
    </Modal>
  );
}
